/**
 * PLP product-card image carousel for Advanced Commerce / GrapheneHC cards.
 * Live for 100% — expects .plp-CardCarousel_Slide (or __Slide) inside .card__media
 * (legacy .card__media-ab-test wrapper still promoted if AC has not removed it yet).
 * Desktop: prev/next arrows. Mobile (≤900px): dots + swipe.
 * Image click → PDP; arrow/dot click → change slide (not navigate).
 */

const SLIDE_SELECTOR = '.plp-CardCarousel_Slide, .plp-CardCarousel__Slide';
const AB_TEST_SELECTOR = '.card__media-ab-test';
const CONTROL_SELECTOR =
  '[data-plp-carousel-prev], [data-plp-carousel-next], [data-plp-carousel-dot], .plp-CardCarousel_Button, .plp-CardCarousel_Dot, .plp-CardCarousel_Nav, .plp-CardCarousel_Dots';
const CHEVRON_PREV =
  '<svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none"><path d="M5.85414 10.1465C5.9006 10.193 5.93745 10.2481 5.96259 10.3088C5.98773 10.3695 6.00067 10.4346 6.00067 10.5003C6.00067 10.566 5.98773 10.631 5.96259 10.6917C5.93745 10.7524 5.9006 10.8076 5.85414 10.854C5.80769 10.9005 5.75254 10.9373 5.69184 10.9625C5.63115 10.9876 5.56609 11.0006 5.50039 11.0006C5.4347 11.0006 5.36964 10.9876 5.30895 10.9625C5.24825 10.9373 5.1931 10.9005 5.14664 10.854L0.146643 5.85403C0.100155 5.80759 0.0632757 5.75245 0.0381136 5.69175C0.0129514 5.63105 0 5.56599 0 5.50028C0 5.43457 0.0129514 5.36951 0.0381136 5.30881C0.0632757 5.24811 0.100155 5.19296 0.146643 5.14653L5.14664 0.146528C5.24046 0.0527077 5.36771 -2.61548e-09 5.50039 0C5.63308 2.61548e-09 5.76032 0.0527077 5.85414 0.146528C5.94796 0.240348 6.00067 0.367596 6.00067 0.500278C6.00067 0.63296 5.94796 0.760208 5.85414 0.854028L1.20727 5.50028L5.85414 10.1465Z" fill="white"/></svg>';
const CHEVRON_NEXT =
  '<svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none"><path d="M0.14659 0.854021C0.100135 0.807565 0.0632848 0.752415 0.0381436 0.691719C0.0130023 0.631022 6.14705e-05 0.565968 6.14763e-05 0.50027C6.1482e-05 0.434573 0.0130024 0.369519 0.0381436 0.308822C0.0632849 0.248126 0.100135 0.192975 0.14659 0.14652C0.193045 0.100065 0.248195 0.0632157 0.308892 0.038074C0.369588 0.0129323 0.434642 -8.116e-06 0.50034 -8.11025e-06C0.566038 -8.10451e-06 0.631092 0.0129323 0.691788 0.038074C0.752485 0.0632158 0.807635 0.100065 0.85409 0.14652L5.85409 5.14652C5.90058 5.19296 5.93746 5.2481 5.96262 5.3088C5.98778 5.3695 6.00073 5.43456 6.00073 5.50027C6.00073 5.56598 5.98778 5.63104 5.96262 5.69174C5.93746 5.75244 5.90058 5.80759 5.85409 5.85402L0.854089 10.854C0.760269 10.9478 0.633021 11.0005 0.500339 11.0005C0.367657 11.0005 0.240409 10.9478 0.146589 10.854C0.0527687 10.7602 6.05905e-05 10.633 6.06021e-05 10.5003C6.06137e-05 10.3676 0.0527688 10.2403 0.146589 10.1465L4.79346 5.50027L0.14659 0.854021Z" fill="white"/></svg>';

function getProductUrl(carousel) {
  const cardInner = carousel.closest('.card__inner');
  const card = carousel.closest('.card, .card-wrapper, .grid__item');

  return (
    cardInner?.querySelector(':scope > .card__media_href')?.getAttribute('href') ||
    carousel.closest('.card__media')?.querySelector('.card__media_href')?.getAttribute('href') ||
    card?.querySelector('.card__heading a[href*="/products/"]')?.getAttribute('href') ||
    card?.querySelector('a[href*="/products/"]')?.getAttribute('href') ||
    ''
  );
}

function stopCardNavigation(event) {
  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }
}

function getDirectSlides(parent) {
  return [...parent.children].filter((el) => el.matches?.(SLIDE_SELECTOR));
}

function ensureTrack(carousel) {
  let track = carousel.querySelector('[data-plp-carousel-track], .plp-CardCarousel_Track');

  if (!track) {
    track = document.createElement('div');
    track.className = 'plp-CardCarousel_Track';
    track.setAttribute('data-plp-carousel-track', '');

    const slides = getDirectSlides(carousel).length
      ? getDirectSlides(carousel)
      : [...carousel.querySelectorAll(SLIDE_SELECTOR)];

    for (const slide of slides) {
      track.appendChild(slide);
    }

    carousel.insertBefore(track, carousel.firstChild);
  }

  return track;
}

function ensureCarouselShell(media) {
  media.classList.add('plp-CardCarousel-host');
  media.closest('.card__inner')?.classList.add('plp-CardCarousel-card');

  const abTest = media.querySelector(`:scope > ${AB_TEST_SELECTOR}`);
  let carousel = media.querySelector('.plp-CardCarousel');

  // Legacy A/B wrapper: promote .card__media-ab-test to carousel root
  if (!carousel && abTest) {
    abTest.classList.add('plp-CardCarousel');
    abTest.setAttribute('data-plp-carousel', '');
    abTest.setAttribute('data-ab-plp-media', 'carousel');
    carousel = abTest;
  }

  const looseSlides = getDirectSlides(media);

  if (!carousel && looseSlides.length) {
    carousel = document.createElement('div');
    carousel.className = 'plp-CardCarousel';
    carousel.setAttribute('data-plp-carousel', '');
    carousel.setAttribute('data-ab-plp-media', 'carousel');

    const track = document.createElement('div');
    track.className = 'plp-CardCarousel_Track';
    track.setAttribute('data-plp-carousel-track', '');

    for (const slide of looseSlides) {
      track.appendChild(slide);
    }

    const href = media.querySelector(':scope > .card__media_href');
    const content = media.querySelector(':scope > .card__content');

    if (href) {
      href.insertAdjacentElement('afterend', carousel);
    } else if (content) {
      media.insertBefore(carousel, content);
    } else {
      media.appendChild(carousel);
    }

    carousel.appendChild(track);
  }

  if (!carousel) return null;

  carousel.removeAttribute('hidden');
  ensureTrack(carousel);

  return carousel;
}

function ensureControls(carousel, slideCount) {
  let nav = carousel.querySelector('.plp-CardCarousel_Nav');
  let dots = carousel.querySelector('.plp-CardCarousel_Dots');

  if (slideCount < 2) {
    carousel.classList.add('plp-CardCarousel--single');
    nav?.remove();
    dots?.remove();
    return { nav: null, dots: null };
  }

  carousel.classList.remove('plp-CardCarousel--single');

  if (!nav) {
    nav = document.createElement('div');
    nav.className = 'plp-CardCarousel_Nav';
    nav.setAttribute('data-plp-carousel-nav', '');

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'plp-CardCarousel_Button plp-CardCarousel_Button--prev';
    prev.setAttribute('aria-label', 'Previous image');
    prev.setAttribute('data-plp-carousel-prev', '');
    prev.innerHTML = CHEVRON_PREV;

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'plp-CardCarousel_Button plp-CardCarousel_Button--next';
    next.setAttribute('aria-label', 'Next image');
    next.setAttribute('data-plp-carousel-next', '');
    next.innerHTML = CHEVRON_NEXT;

    nav.append(prev, next);
    carousel.appendChild(nav);
  }

  if (!dots) {
    dots = document.createElement('div');
    dots.className = 'plp-CardCarousel_Dots';
    dots.setAttribute('data-plp-carousel-dots', '');
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Product images');
    carousel.appendChild(dots);
  }

  if (dots.children.length !== slideCount) {
    dots.replaceChildren();

    for (let i = 0; i < slideCount; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'plp-CardCarousel_Dot';
      dot.setAttribute('aria-label', `Go to image ${i + 1}`);
      dot.setAttribute('data-plp-carousel-dot', String(i));
      dots.appendChild(dot);
    }
  }

  return { nav, dots };
}

function setActiveSlide(carousel, index) {
  const slides = [...carousel.querySelectorAll(SLIDE_SELECTOR)];
  if (!slides.length) return;

  const nextIndex = ((index % slides.length) + slides.length) % slides.length;

  slides.forEach((slide, i) => {
    const active = i === nextIndex;
    slide.classList.toggle('is-active', active);

    const img = slide.querySelector('img');
    if (img && active && img.loading === 'lazy') {
      img.loading = 'eager';
    }
  });

  carousel.querySelectorAll('.plp-CardCarousel_Dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === nextIndex);
    dot.setAttribute('aria-current', i === nextIndex ? 'true' : 'false');
  });

  carousel.dataset.plpCarouselIndex = String(nextIndex);
}

function isPlpCarouselLayerVisible(carousel) {
  if (!carousel || carousel.hasAttribute('hidden')) return false;

  try {
    const style = window.getComputedStyle(carousel);
    return style.display !== 'none' && style.visibility !== 'hidden';
  } catch (_) {
    return false;
  }
}

function syncPlpCarouselActiveState(carousel) {
  const active = isPlpCarouselLayerVisible(carousel);
  const media = carousel.closest('.card__media');
  const cardInner = carousel.closest('.card__inner');

  media?.classList.toggle('plp-CardCarousel-host--active', active);
  cardInner?.classList.toggle('plp-CardCarousel-card--active', active);
  carousel.classList.toggle('plp-CardCarousel--active', active);

  return active;
}

function observePlpCarouselVisibility(carousel) {
  if (carousel.dataset.plpCarouselVisibilityObserved === 'true') return;
  carousel.dataset.plpCarouselVisibilityObserved = 'true';

  const sync = () => syncPlpCarouselActiveState(carousel);

  sync();

  try {
    const observer = new MutationObserver(sync);
    observer.observe(carousel, {
      attributes: true,
      attributeFilter: ['style', 'hidden', 'class'],
    });
  } catch (_) {
    /* ignore */
  }
}

function canHoverPreview() {
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch (_) {
    return false;
  }
}

function getVideoSourceAttr(video) {
  if (!video) return '';

  const direct = (video.getAttribute('src') || '').trim();
  if (direct) return direct;

  const source = video.querySelector('source[src]');
  return (source?.getAttribute('src') || '').trim();
}

function getHoverVideo(media) {
  const candidates = [
    ...media.querySelectorAll(':scope > .custom-video-tag, :scope > .media.media--hover-effect'),
  ];

  for (const tag of candidates) {
    const video = tag.querySelector('video');
    if (video && getVideoSourceAttr(video)) return { tag, video };
  }

  return null;
}

function showHoverVideo(media, video) {
  const token = String(Date.now());
  media.dataset.plpHoverVideoToken = token;

  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.preload = 'auto';
  // lazy + visibility:hidden often never fetches — force a real load on hover
  if (video.getAttribute('loading') === 'lazy') {
    video.removeAttribute('loading');
  }

  const reveal = () => {
    if (media.dataset.plpHoverVideoToken !== token) return;
    media.classList.add('is-plp-hover-video');
  };

  const tryPlay = () => {
    if (media.dataset.plpHoverVideoToken !== token) return;

    try {
      const playPromise = video.play();
      if (playPromise?.then) {
        // Only cover the carousel once a frame is actually playing (avoids black flash).
        playPromise.then(reveal).catch(() => {});
      } else {
        reveal();
      }
    } catch (_) {
      /* ignore */
    }
  };

  if (video.readyState >= 2) {
    tryPlay();
  } else {
    video.addEventListener('canplay', tryPlay, { once: true });
    try {
      video.load();
    } catch (_) {
      /* ignore */
    }
  }
}

function hideHoverVideo(media, video) {
  media.dataset.plpHoverVideoToken = '';
  media.classList.remove('is-plp-hover-video');

  try {
    video.pause();
    if (video.currentTime) video.currentTime = 0;
  } catch (_) {
    /* ignore */
  }
}

function dismissHoverVideo(media) {
  if (!media?.classList.contains('is-plp-hover-video')) return false;

  const hoverVideo = getHoverVideo(media);
  if (hoverVideo) hideHoverVideo(media, hoverVideo.video);
  else media.classList.remove('is-plp-hover-video');

  return true;
}

function bindHoverPreview(carousel) {
  const media = carousel.closest('.card__media');
  if (!media || media.dataset.plpCarouselHoverBound === 'true') return;
  media.dataset.plpCarouselHoverBound = 'true';

  const card =
    media.closest('.card-wrapper, .product-card-wrapper') ||
    media.closest('.card') ||
    media;

  card.addEventListener('pointerenter', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!canHoverPreview()) return;
    if (!syncPlpCarouselActiveState(carousel)) return;

    const hoverVideo = getHoverVideo(media);

    if (hoverVideo) {
      showHoverVideo(media, hoverVideo.video);
      return;
    }

    setActiveSlide(carousel, 1);
  });

  card.addEventListener('pointerleave', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!canHoverPreview()) return;

    const hoverVideo = getHoverVideo(media);
    if (hoverVideo) hideHoverVideo(media, hoverVideo.video);
    else media.classList.remove('is-plp-hover-video');

    setActiveSlide(carousel, 0);
  });

  media.addEventListener('click', (event) => {
    if (!media.classList.contains('is-plp-hover-video')) return;
    if (!event.target.closest('.custom-video-tag, .media.media--hover-effect')) return;

    const url = getProductUrl(carousel);
    if (!url) return;

    stopCardNavigation(event);
    window.location.href = url;
  });
}

function bindCarousel(carousel) {
  if (carousel.dataset.plpCarouselBound === 'true') return;

  const slides = [...carousel.querySelectorAll(SLIDE_SELECTOR)];
  if (!slides.length) return;

  ensureControls(carousel, slides.length);
  observePlpCarouselVisibility(carousel);

  if (!slides.some((slide) => slide.classList.contains('is-active'))) {
    slides[0].classList.add('is-active');
  }

  const initial = slides.findIndex((slide) => slide.classList.contains('is-active'));
  setActiveSlide(carousel, initial < 0 ? 0 : initial);

  const go = (delta) => {
    const media = carousel.closest('.card__media');

    // Video stands in for "image 2". Next → carousel slide 1 (3rd view); prev → slide 0.
    if (dismissHoverVideo(media)) {
      setActiveSlide(carousel, delta > 0 ? 1 : 0);
      return;
    }

    const current = Number(carousel.dataset.plpCarouselIndex || 0);
    setActiveSlide(carousel, current + delta);
  };

  const onControlActivate = (event) => {
    if (!syncPlpCarouselActiveState(carousel)) return;

    const target = event.target.closest(
      '[data-plp-carousel-prev], [data-plp-carousel-next], [data-plp-carousel-dot]'
    );
    if (!target || !carousel.contains(target)) return;

    stopCardNavigation(event);

    if (target.hasAttribute('data-plp-carousel-prev')) {
      go(-1);
      return;
    }

    if (target.hasAttribute('data-plp-carousel-next')) {
      go(1);
      return;
    }

    const dotIndex = Number(target.getAttribute('data-plp-carousel-dot'));
    if (!Number.isNaN(dotIndex)) {
      dismissHoverVideo(carousel.closest('.card__media'));
      setActiveSlide(carousel, dotIndex);
    }
  };

  carousel.addEventListener('click', onControlActivate, true);
  carousel.addEventListener('auxclick', onControlActivate, true);
  carousel.addEventListener(
    'pointerdown',
    (event) => {
      if (!syncPlpCarouselActiveState(carousel)) return;
      if (event.target.closest(CONTROL_SELECTOR)) stopCardNavigation(event);
    },
    true
  );

  carousel.addEventListener('click', (event) => {
    // Hidden carousel: do not intercept — card__media_href must navigate
    if (!syncPlpCarouselActiveState(carousel)) return;
    if (event.target.closest(CONTROL_SELECTOR)) return;

    const url = getProductUrl(carousel);
    if (!url) return;

    stopCardNavigation(event);
    window.location.href = url;
  });

  let touchStartX = 0;
  let touchStartY = 0;

  carousel.addEventListener(
    'touchstart',
    (event) => {
      if (!syncPlpCarouselActiveState(carousel)) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    },
    { passive: true }
  );

  carousel.addEventListener(
    'touchend',
    (event) => {
      if (!syncPlpCarouselActiveState(carousel)) return;
      if (event.target.closest(CONTROL_SELECTOR)) return;

      const touch = event.changedTouches?.[0];
      if (!touch) return;

      const dx = touch.clientX - touchStartX;
      const dy = touch.clientY - touchStartY;

      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;

      stopCardNavigation(event);
      go(dx < 0 ? 1 : -1);
    },
    { passive: false }
  );

  bindHoverPreview(carousel);

  carousel.dataset.plpCarouselBound = 'true';
}

function initPlpCardCarousels(root = document) {
  try {
    const mediaNodes = root.querySelectorAll('.card__media');

    for (const media of mediaNodes) {
      const hasSlides =
        media.querySelector(SLIDE_SELECTOR) ||
        media.querySelector('.plp-CardCarousel') ||
        media.querySelector(AB_TEST_SELECTOR);
      if (!hasSlides) continue;

      const carousel = ensureCarouselShell(media);
      if (!carousel) continue;

      if (
        carousel.dataset.plpCarouselBound === 'true' &&
        !carousel.querySelector('[data-plp-carousel-nav], .plp-CardCarousel_Nav')
      ) {
        delete carousel.dataset.plpCarouselBound;
      }

      bindCarousel(carousel);
    }
  } catch (_) {
    /* fail soft — never break PLP */
  }
}

function schedulePlpCardCarousels(root = document) {
  initPlpCardCarousels(root);
  window.requestAnimationFrame(() => initPlpCardCarousels(root));

  // Intelligems may show the carousel via stylesheet without changing inline style —
  // re-check computed visibility a few times after paint.
  [100, 500, 1500].forEach((ms) => {
    window.setTimeout(() => {
      document.querySelectorAll('.plp-CardCarousel').forEach((carousel) => {
        syncPlpCarouselActiveState(carousel);
      });
    }, ms);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => schedulePlpCardCarousels());
} else {
  schedulePlpCardCarousels();
}

document.addEventListener('swym:collections-loaded', () => {
  schedulePlpCardCarousels(
    document.querySelector('.acs_collection_results, .acs_results') || document
  );
});

window.initPlpCardCarousels = initPlpCardCarousels;
window.schedulePlpCardCarousels = schedulePlpCardCarousels;
